import React, { useEffect, useState, useCallback } from 'react';
import LoadingScreen from '../components/ui/LoadingScreen';
import { LCTF_PORTAL_BRAND_LOGO_SRC } from '../constants/lctfBrandAssets';
import { ensureAuth } from '../utils/firebase';
import {
  fetchQuestionnairePublicContent,
  QuestionnairePublicContent,
} from '../utils/questionnairePublicContent';
import {
  PublicFrameStylesSection,
  PublicTimberSpeciesSection,
  PublicStyleGuideLightbox,
} from '../components/portal/PublicStyleGuideSections';
import '../components/portal/DesignQuestionnaire.css';
import './StructuralStylesPage.css';

const StructuralStylesPage: React.FC = () => {
  const [content, setContent] = useState<QuestionnairePublicContent | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    document.title = "Structural Styles | Leiper's Creek Timber Frames";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureAuth();
      const loaded = await fetchQuestionnairePublicContent();
      if (!cancelled) setContent(loaded);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openLightbox = useCallback((url: string, title: string) => {
    setLightbox({ url, title });
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  if (!content) {
    return <LoadingScreen message="Loading..." size="medium" />;
  }

  return (
    <div className="dq-container structural-styles-page">
      <header className="structural-styles-header">
        <img
          src={LCTF_PORTAL_BRAND_LOGO_SRC}
          alt="Leiper's Creek Timber Frames"
          className="structural-styles-logo"
        />
        <h1 className="structural-styles-heading">Structural Styles</h1>
      </header>

      <PublicFrameStylesSection frameStyles={content.frameStyles} onEnlarge={openLightbox} />
      <PublicTimberSpeciesSection timberSpecies={content.timberSpecies} />

      <footer className="structural-styles-footer">
        <a href="https://lctimberframes.com/contact" target="_blank" rel="noopener noreferrer">
          Contact us
        </a>
      </footer>

      <PublicStyleGuideLightbox lightbox={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
};

export default StructuralStylesPage;
